import React, { useId, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import { OutlinedInputProps } from '@mui/material/OutlinedInput';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTheme } from '@mui/material/styles';
import { VarName, varNameDetails } from '../../utils/varNames';
import useStyles from '../../styles/alertForm';
import ThresholdFormSection from './ThresholdFormSection';

import { Analysisoptions, Analysisoptions1, Threshold } from '../../services/api';

export type AnalysisOptions = Analysisoptions | Analysisoptions1;
type HistoryAnalysisOptions = Extract<
  AnalysisOptions,
  { analysisType: 'locationHistory' | 'sensorHistory' }
>;

interface AnalysisPeriodFormSectionProps {
  isLocation: boolean;
  value: AnalysisOptions;
  onChange: (value: Partial<AnalysisOptions>) => void;
  allowEditing: boolean;
}

const LOCATION_ANALYSIS_TYPES = [
  { value: 'locationRecent' as const, label: 'Location Recent' },
  { value: 'locationHistory' as const, label: 'Location History' },
];

const SENSOR_ANALYSIS_TYPES = [
  { value: 'sensorRecent' as const, label: 'Sensor Recent' },
  { value: 'sensorHistory' as const, label: 'Sensor History' },
];

function AnalysisPeriodFormSection({
  isLocation,
  value,
  onChange,
  allowEditing,
}: AnalysisPeriodFormSectionProps) {
  const uid = useId();
  const { classes } = useStyles();
  const theme = useTheme();

  const hValue = value as HistoryAnalysisOptions;
  const locValue = isLocation ? (value as Analysisoptions) : null;

  // Raw string states for comma-separated fields
  const [rawHours, setRawHours] = useState(hValue.hoursOfDayFilter?.join(', ') ?? '');
  const [rawDays, setRawDays] = useState(hValue.daysOfWeekFilter?.join(', ') ?? '');
  const [showSensorFilter, setShowSensorFilter] = useState(false);
  const [rawWhitelist, setRawWhitelist] = useState(locValue?.whiteListSensors?.join(', ') ?? '');
  const [rawBlacklist, setRawBlacklist] = useState(locValue?.blackListSensors?.join(', ') ?? '');

  const handleChange = (key: string, val: unknown) => {
    onChange({ [key]: val } as Partial<AnalysisOptions>);
  };

  const handleLocationChange = <K extends keyof Analysisoptions>(
    key: K,
    val: Analysisoptions[K]
  ) => {
    (onChange as (value: Partial<Analysisoptions>) => void)({ [key]: val });
  };

  const handleCommaSeparated = (
    key: 'hoursOfDayFilter' | 'daysOfWeekFilter',
    raw: string,
    setRaw: (s: string) => void
  ) => {
    setRaw(raw);
    handleChange(
      key,
      raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map(Number)
        .filter((n) => !Number.isNaN(n))
    );
  };

  const handleLocationCommaSeparated = (
    key: 'whiteListSensors' | 'blackListSensors',
    raw: string,
    setRaw: (s: string) => void
  ) => {
    setRaw(raw);
    handleLocationChange(
      key,
      raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    );
  };

  function getTooltipSlotProps(desc: string): { input: Partial<OutlinedInputProps> } {
    return {
      input: {
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={desc} placement="top" arrow>
              <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} color="secondary" />
            </Tooltip>
          </InputAdornment>
        ),
      },
    };
  }

  const analysisTypes = isLocation ? LOCATION_ANALYSIS_TYPES : SENSOR_ANALYSIS_TYPES;
  const isHistoryType =
    value.analysisType === 'locationHistory' || value.analysisType === 'sensorHistory';

  return (
    <Box className={classes.formContainer}>
      <InputLabel sx={{ mb: 2 }}>Analysis Options</InputLabel>
      <Grid container spacing={2} sx={{ mt: 4 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small">
            <Tooltip title="Description of the analysis type" placement="top" arrow>
              <InputLabel id={`${uid}-analysisType`} sx={{ display: 'flex', alignItems: 'center' }}>
                Analysis Type <HelpOutlineIcon sx={{ fontSize: '1rem', ml: 1 }} />
              </InputLabel>
            </Tooltip>
            <Select
              labelId={`${uid}-analysisType`}
              label={
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  Analysis Type
                  <HelpOutlineIcon sx={{ fontSize: '1rem' }} />
                </Box>
              }
              value={value.analysisType}
              onChange={(e) => handleChange('analysisType', e.target.value)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.contrastText,
                },
              }}
              disabled={!allowEditing}
            >
              {analysisTypes.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small">
            <Tooltip title="Select source" placement="top" arrow>
              <InputLabel id={`${uid}-varName`} sx={{ display: 'flex', alignItems: 'center' }}>
                Variable Name <HelpOutlineIcon sx={{ fontSize: '1rem', ml: 1 }} />
              </InputLabel>
            </Tooltip>
            <Select
              labelId={`${uid}-varName`}
              label="Variable Name"
              value={value.varName}
              onChange={(e) => handleChange('varName', e.target.value as VarName)}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.contrastText,
                },
              }}
              disabled={!allowEditing}
            >
              {Object.values(VarName).map((v) => (
                <MenuItem key={v} value={v}>
                  {varNameDetails[v]?.label ?? v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, sm: 12 }}>
          <ThresholdFormSection
            isHistoryType={isHistoryType}
            value={value.threshold}
            onChange={(threshold: Threshold) => onChange({ threshold })}
            allowEditing={allowEditing}
          />
        </Grid>

        {isHistoryType && (
          <>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Analysis Period (days)"
                type="number"
                value={hValue.analysisPeriodDays ?? ''}
                onChange={(e) =>
                  handleChange(
                    'analysisPeriodDays',
                    e.target.value === '' ? undefined : Number(e.target.value)
                  )
                }
                placeholder="e.g. 7"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.contrastText,
                  },
                }}
                slotProps={getTooltipSlotProps('Previous number of days')}
                disabled={!allowEditing}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Hours Limit"
                type="number"
                value={hValue.hoursLimit ?? ''}
                onChange={(e) =>
                  handleChange(
                    'hoursLimit',
                    e.target.value === '' ? undefined : Number(e.target.value)
                  )
                }
                placeholder="e.g. 4"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.contrastText,
                  },
                }}
                slotProps={getTooltipSlotProps(
                  'Number of hours allowed for threshold (exceeding should result in alert)'
                )}
                disabled={!allowEditing}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Hours of Day Filter"
                value={rawHours}
                onChange={(e) =>
                  handleCommaSeparated('hoursOfDayFilter', e.target.value, setRawHours)
                }
                placeholder="e.g. 7, 8, 9, 19, 20, 21"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.contrastText,
                  },
                }}
                slotProps={getTooltipSlotProps('Hours to inspect, eg: [7,8,9,19,20,21]')}
                disabled={!allowEditing}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Days of Week Filter"
                value={rawDays}
                onChange={(e) =>
                  handleCommaSeparated('daysOfWeekFilter', e.target.value, setRawDays)
                }
                placeholder="e.g. 1, 2, 3, 4, 5"
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.contrastText,
                  },
                }}
                slotProps={getTooltipSlotProps(
                  'Days to inspect in week, eg: [1,2,3,4,5] for weekdays, numbers from 0 (Sunday) to 6 (Saturday)'
                )}
                disabled={!allowEditing}
              />
            </Grid>
          </>
        )}

        {isLocation && (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={showSensorFilter}
                  onChange={() => setShowSensorFilter(!showSensorFilter)}
                  disabled={!allowEditing}
                />
              }
              label="Show SensorFilter"
            />
            {showSensorFilter && (
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Sensors Limit (optional)"
                    type="number"
                    value={locValue?.sensorsLimit ?? ''}
                    onChange={(e) =>
                      handleLocationChange(
                        'sensorsLimit',
                        e.target.value === '' ? undefined : Number(e.target.value)
                      )
                    }
                    placeholder="e.g. 10"
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.contrastText,
                      },
                    }}
                    slotProps={getTooltipSlotProps(
                      'Number of sensors that must match before alerting'
                    )}
                    disabled={!allowEditing}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Whitelist Sensors (optional)"
                    value={rawWhitelist}
                    onChange={(e) =>
                      handleLocationCommaSeparated(
                        'whiteListSensors',
                        e.target.value,
                        setRawWhitelist
                      )
                    }
                    placeholder="sensor-id-1, sensor-id-2"
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.contrastText,
                      },
                    }}
                    slotProps={getTooltipSlotProps(
                      'Sensor ids in this list will not be considered'
                    )}
                    disabled={!allowEditing}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Blacklist Sensors (optional)"
                    value={rawBlacklist}
                    onChange={(e) =>
                      handleLocationCommaSeparated(
                        'blackListSensors',
                        e.target.value,
                        setRawBlacklist
                      )
                    }
                    placeholder="sensor-id-3, sensor-id-4"
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.contrastText,
                      },
                    }}
                    slotProps={getTooltipSlotProps('Description of the analysis type')}
                    disabled={!allowEditing}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        )}

        {isHistoryType && (
          <Grid size={{ xs: 12 }} sx={{ ml: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={hValue.alertIfNotMatched ?? false}
                  onChange={(e) => handleChange('alertIfNotMatched', e.target.checked)}
                  color="success"
                  disabled={!allowEditing}
                />
              }
              label="Alert if not matched (invert threshold)"
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default AnalysisPeriodFormSection;
